import React, { useMemo } from "react";
import {
  CircleHelp,
  LogOut,
  type LucideIcon,
  Mail,
  MessageCircle,
  Settings,
  Users
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  FacebookIcon,
  InstagramIcon,
  LinkedinIcon
} from "@/components/icons/brands";
import { cn } from "@/lib/utils";
import { useLogOut } from "../hooks/useLogOut";
import { auth } from "../api/firebaseApi";
import { useGetUserDetails } from "../hooks/useGetUserDetails";
import {
  isSupportChatAvailable,
  openSupport,
  SUPPORT_EMAIL
} from "../helpers/helpers";
import { normalizeAvatarSrc } from "../helpers/userDisplay";

type Props = {
  onRequestClose?: () => void;
  /** When false, omit avatar / name / email (e.g. already shown on the side nav). Default true. */
  showProfileHeader?: boolean;
  onOpenSettings?: () => void;
  /**
   * `floating` (default): self-contained card with border/shadow, scaled to
   * 88% to fit the header anchor. `embedded`: no chrome; the parent (e.g. a
   * shadcn PopoverContent) supplies border, radius and shadow.
   */
  variant?: "floating" | "embedded";
};

type MenuTileProps = {
  label: string;
  icon?: LucideIcon;
  iconElement?: React.ReactNode;
  onClick: () => void;
};

const MenuTile = ({
  label,
  icon: Icon,
  iconElement,
  onClick
}: MenuTileProps) => {
  return (
    <button
      type="button"
      className="group flex w-full cursor-pointer flex-col items-center gap-2"
      onClick={onClick}
    >
      <div className="flex size-16 flex-none items-center justify-center rounded-md bg-fill-1 transition-colors duration-100 group-hover:bg-fill-2">
        {iconElement ?? (Icon ? <Icon className="size-6 text-fg" /> : null)}
      </div>
      <span className="text-sm text-fg-2 group-hover:text-fg">{label}</span>
    </button>
  );
};

type SocialIconProps = {
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  iconElement?: React.ReactNode;
  href?: string;
  mailto?: string;
  onClick?: () => void;
};

const SocialIcon = ({
  label,
  icon: Icon,
  iconElement,
  href,
  mailto,
  onClick
}: SocialIconProps) => {
  const handleClick = () => {
    if (mailto && typeof window !== "undefined") {
      window.location.href = mailto;
      onClick?.();
      return;
    }
    if (href && typeof window !== "undefined") {
      window.open(href, "_blank", "noopener,noreferrer");
      onClick?.();
      return;
    }
    onClick?.();
  };

  return (
    <button
      type="button"
      aria-label={label}
      className="flex h-8 w-8 items-center justify-center rounded-md text-fg hover:bg-fill-1 active:bg-fill-1"
      onClick={handleClick}
    >
      {iconElement ?? (Icon ? <Icon className="size-4" /> : null)}
    </button>
  );
};

export const UserMenuItems = ({
  onRequestClose,
  showProfileHeader = true,
  onOpenSettings,
  variant = "floating"
}: Props) => {
  const navigate = useNavigate();

  const { onSubmitLogOut } = useLogOut();
  const {
    userDetails: { userType, id, userFullName, email, avatarSrc }
  } = useGetUserDetails();

  const normalizedAvatarSrc = useMemo(
    () => normalizeAvatarSrc(avatarSrc),
    [avatarSrc]
  );

  const handleSignOut = async () => {
    if (auth.currentUser) {
      await onSubmitLogOut();
    } else {
      navigate("/login");
    }
    onRequestClose?.();
  };

  const profilePath = useMemo(() => {
    return userType === "Lawyer"
      ? "/cases"
      : userType === "Individual"
      ? `/individual/${id}/account/profile`
      : userType === "ForeignNational"
      ? "/portal"
      : null;
  }, [userType, id]);

  const initials = useMemo((): string => {
    const parts = userFullName.trim().split(/\s+/).filter(Boolean);
    if (parts.length === 0) return "";
    if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
    return `${parts[0][0] ?? ""}${
      parts[parts.length - 1][0] ?? ""
    }`.toUpperCase();
  }, [userFullName]);

  const openExternal = (url: string) => {
    if (typeof window === "undefined") return;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const handleHelp = () => {
    openExternal(
      "https://help.caseblink.com/en/collections/9448854-caseblink-help-center"
    );
    onRequestClose?.();
  };

  const handleSettings = () => {
    // Prefer opening the ChatGPT-style settings modal (lawyers only).
    if (userType === "Lawyer" && onOpenSettings) {
      onOpenSettings();
      onRequestClose?.();
      return;
    }
    // Fallback: navigate to existing account/profile pages.
    if (profilePath) {
      navigate(profilePath);
      onRequestClose?.();
      return;
    }
    navigate("/cases");
    onRequestClose?.();
  };

  const isTeamEnabled = import.meta.env.REACT_APP_ENABLE_TEAM_MENU === "true";

  const handleTeam = () => {
    if (email.toLowerCase().endsWith("@caseblink.com")) {
      navigate("/admin/users");
      onRequestClose?.();
      return;
    }
    handleHelp();
  };

  const displayEmail = email || auth.currentUser?.email || "—";
  // The menu mounts when it opens, after the Messenger has had time to boot.
  const supportChat = isSupportChatAvailable();

  const renderHeaderInner = () => {
    return (
      <>
        <Avatar size="lg">
          {normalizedAvatarSrc ? (
            <AvatarImage src={normalizedAvatarSrc} alt="" />
          ) : null}
          <AvatarFallback className="text-md">{initials}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col items-start text-left">
          <span className="w-full truncate text-left text-md font-medium text-fg">
            {userFullName || "—"}
          </span>
          <span className="w-full truncate text-left text-sm text-fg-3">
            {displayEmail}
          </span>
        </div>
      </>
    );
  };

  return (
    <div
      className={cn(
        "flex w-80 flex-none flex-col items-start",
        variant === "floating" &&
          "origin-top-right scale-88 rounded-lg bg-elevated shadow-popover ring-1 ring-line-3"
      )}
    >
      {showProfileHeader ? (
        <>
          {profilePath ? (
            <Link
              to={profilePath}
              className="flex w-full min-w-0 items-center gap-4 px-6 py-4"
              onClick={() => onRequestClose?.()}
            >
              {renderHeaderInner()}
            </Link>
          ) : (
            <div className="flex w-full min-w-0 items-center gap-4 px-6 py-4">
              {renderHeaderInner()}
            </div>
          )}

          <div className="h-px w-full flex-none bg-line-1" />
        </>
      ) : null}

      <div className="flex w-full flex-col items-start gap-4 px-4 py-4">
        <div className="grid w-full grid-cols-4 items-start justify-items-center gap-4">
          <MenuTile label="Help" icon={CircleHelp} onClick={handleHelp} />
          {isTeamEnabled ? (
            <MenuTile label="Team" icon={Users} onClick={handleTeam} />
          ) : null}
          <MenuTile label="Settings" icon={Settings} onClick={handleSettings} />
          <MenuTile label="Sign Out" icon={LogOut} onClick={handleSignOut} />
        </div>
      </div>

      <div className="h-px w-full flex-none bg-line-1" />

      <div className="flex w-full items-center justify-center gap-6 px-6 py-4">
        {supportChat ? (
          <SocialIcon
            label="Contact Support"
            icon={MessageCircle}
            onClick={() => {
              openSupport();
              onRequestClose?.();
            }}
          />
        ) : (
          <SocialIcon
            label="Email Support"
            icon={Mail}
            mailto={`mailto:${SUPPORT_EMAIL}`}
            onClick={() => onRequestClose?.()}
          />
        )}
        <SocialIcon
          label="LinkedIn"
          icon={LinkedinIcon}
          href="https://www.linkedin.com/company/caseblink"
          onClick={() => onRequestClose?.()}
        />
        <SocialIcon
          label="Instagram"
          icon={InstagramIcon}
          href="https://www.instagram.com/caseblink.ai"
          onClick={() => onRequestClose?.()}
        />
        <SocialIcon
          label="Facebook"
          icon={FacebookIcon}
          href="https://www.facebook.com/profile.php?id=61572123826634"
          onClick={() => onRequestClose?.()}
        />
      </div>
    </div>
  );
};
